import {
  getRecommendationById,
  requestRecommendation,
  resetRecommendationOptions,
  updateRecommendationOptions,
} from '@/api'
import {
  useDebouncedQueryLoadingByKeys,
  useInvalidateOnSuccessMutation,
} from '@/utils'
import { useQuery } from 'react-query'

const keys = {
  recommendation: 'recommendation',
  recommendationSearchConfig: 'recommendationSearchConfig',
}

export const useRecommendation = (
  id: string,
  options: { enabled?: boolean } = {},
) =>
  useQuery([keys.recommendation, id], () => getRecommendationById(id), {
    // TODO increase stale time globally https://linear.app/sure-in/issue/ENG-489/increase-staletime-from-500ms-to-5000ms-in-frontend-project
    staleTime: 5000,
    ...options,
    select: (data) => ({
      ...data,
      isSearchable: false, // remove this once search criteria is ready | this is most likely obsolete - leaving for compatibility now
    }),
  })

export const useRequestRecommendation = (locationId: string) =>
  useInvalidateOnSuccessMutation(
    [['location', locationId]],
    // FIXME: is message even used?
    ({
      categoryId,
      message,
      partnerSource = null,
      assessment,
    }: {
      categoryId: string
      message?: string
      partnerSource?: string | null
      assessment?: any
    }) =>
      requestRecommendation({
        categoryId,
        locationId,
        partnerSource,
        message,
        assessment,
      }),
  )

export const useUpdateRecommendationOptions = (recommendationId: string) =>
  useInvalidateOnSuccessMutation(
    [[keys.recommendation, recommendationId]],
    ({
      recommendationId,
      ...rest
    }: {
      paymentPeriod?: string
      deductible?: string
      amountInsured?: string
      recommendationId: string
      options?: { name: string; value: boolean }[]
    }) =>
      updateRecommendationOptions({
        recommendationId,
        ...rest,
      }),
    [keys.recommendation, recommendationId],
  )

export const useResetRecommendationOptions = (recommendationId: string) =>
  useInvalidateOnSuccessMutation(
    [[keys.recommendation, recommendationId]],
    ({ recommendationId }: { recommendationId: string }) =>
      resetRecommendationOptions({
        recommendationId,
      }),
    [keys.recommendation, recommendationId],
  )

export const useIsRecommendationPriceLoading = (recommendationId: string) => {
  const { isDebouncedLoading } = useDebouncedQueryLoadingByKeys({
    queryKey: [keys.recommendation, recommendationId],
    mutationKey: [keys.recommendation, recommendationId],
    minDuration: 1000,
  })

  return { isRecommendationPriceLoading: isDebouncedLoading }
}
